import { VStack } from '@chakra-ui/react';
import { useFolders } from 'hooks/api/backend/folder/getFolderById';
import { ReactElement, useState } from 'react';
import { Section } from 'components/common/Sections/Section';
import { SectionVariant } from 'components/common/Sections/SectionTitle';
import { BaseArticle, Medication, Folder as TFolder } from '@novaheal/types';
import { MedicationCard } from 'components/common/MedicationCard';
import { ArticleEntry } from 'components/common/ArticleEntry/ArticleEntry';
import { ChevronRightIcon } from 'assets/icons/v2';
import { FolderBox } from 'components/common/SubfolderStack/FolderBox';

type Props = {
  id: string;
};

const MAX_SUBFOLDERS = 2;

export const Folder = ({ id }: Props): ReactElement => {
  const { data: folder } = useFolders(id);

  const hasSubfolders = folder && folder.subfolder && folder.subfolder.length > 0;
  const hasArticles = folder && folder.articles && folder.articles.length > 0;
  const hasMedications = folder && folder.medications && folder.medications.length > 0;

  const [shouldShowLess, setShouldShowLess] = useState(false);

  const filteredFolders = folder?.subfolder?.filter(
    (folder) =>
      folder.totalNumOfArticles > 0 ||
      folder.totalNumOfMedications > 0 ||
      folder.totalNumOfSubfolders > 0
  );
  const numOfSubfolders = filteredFolders?.length || 0;
  const hasShowLessButton = numOfSubfolders > MAX_SUBFOLDERS;
  const slicedSubfolders = shouldShowLess ? filteredFolders?.slice(0, 2) : filteredFolders;

  const buttonContent = (): ReactElement | string => {
    return !shouldShowLess ? (
      'Weniger sehen'
    ) : (
      <>
        Alle ansehen <ChevronRightIcon />
      </>
    );
  };

  return (
    <VStack gap="4xl" align="left" width="100%">
      {hasSubfolders && (
        <Section
          variant={SectionVariant.FOLDER}
          hasButton={hasShowLessButton}
          buttonContent={buttonContent()}
          onClickButton={() => setShouldShowLess((prev) => !prev)}
        >
          {slicedSubfolders?.map(
            (subFolder: TFolder): ReactElement => (
              <FolderBox
                key={subFolder.id}
                numOfArticles={subFolder.totalNumOfArticles}
                numOfSubfolders={subFolder.totalNumOfSubfolders}
                numOfMedications={subFolder.totalNumOfMedications}
                name={subFolder.name}
                id={subFolder.id}
                image={subFolder.image}
              />
            )
          )}
        </Section>
      )}
      {hasArticles &&
        folder.articles?.map((article: BaseArticle) => (
          <ArticleEntry
            key={article.id}
            id={article.id}
            subtitle={article.folder || article.category.title}
            title={article.title}
            hasVideo={article.hasVideo}
            relevantYearNames={article.relevantYears}
            imageUrl={article.coverImageUrl}
          />
        ))}
      {hasMedications && (
        <Section variant={SectionVariant.MEDICATION}>
          {folder.medications?.map(({ id, name, indications, trademark }: Medication) => (
            <MedicationCard
              id={id}
              key={id}
              name={name}
              indications={indications}
              trademark={trademark}
            />
          ))}
        </Section>
      )}
    </VStack>
  );
};
